import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Receipt,
  RefreshCw,
  DollarSign,
  Eye,
  ListChecks,
  ArrowRightLeft,
  Link as LinkIcon,
  Split,
  Combine,
  FileImage,
  Bell,
  Plus,
  Upload,
  Check,
  ChefHat,
  Bike,
} from 'lucide-react';
import type { PedidoVista } from '@shared/types';
import { seLePuedeCobrar, motivoSinCobro } from '@shared/cobroVisible';
import { etiquetaEstadoPedido } from '@shared/estadoPedidoVista';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';
import { useNegocioStore } from '@renderer/store/useNegocioStore';
import { puedeUsarCanal } from '@shared/permisos';
import { useAjustesPermisos } from '@renderer/store/useAjustesPermisos';
import { useSessionStore } from '@renderer/store/useSessionStore';
import { formatPrecio, formatFecha } from '@renderer/utils/formatters';
import { CobroModal } from '@renderer/components/cobro/CobroModal';
import { CobroTransferenciaModal } from '@renderer/components/cobro/CobroTransferenciaModal';
import { TicketPreviewModal } from '@renderer/components/ticket/TicketPreviewModal';
import { DetallePedidoModal } from '@renderer/components/pedidos/DetallePedidoModal';
import { MoverMesaModal } from '@renderer/components/pedidos/MoverMesaModal';
import { JuntarMesasModal } from '@renderer/components/pedidos/JuntarMesasModal';
import { DividirCuentaModal } from '@renderer/components/pedidos/DividirCuentaModal';
import { HelpButton } from '@renderer/components/help/HelpButton';
import { tourPedidosActivos } from '@renderer/tours';

/*
  Sprint 89 — Aquí vivían `ESTADO_COLORES` y `ESTADO_LABEL`, dos diccionarios que traducían
  `Pedido.estado` a un texto. Se fueron a `@shared/estadoPedidoVista` porque el texto no depende
  sólo del estado: 'abierto' significaba "Pago pendiente" aunque el pedido ya estuviera cobrado, y
  esa mentira convivía en la misma fila con un "✓ Ya pagado". Dejarlos aquí muertos invitaba a que
  alguien los volviera a usar.
*/

export function PedidosActivosPage() {
  const navigate = useNavigate();
  const [pedidos, setPedidos] = useState<PedidoVista[]>([]);
  const [cargando, setCargando] = useState(true);
  const [cobroId, setCobroId] = useState<number | null>(null);
  const [previewId, setPreviewId] = useState<number | null>(null);
  const [previewPdfPath, setPreviewPdfPath] = useState<string | null>(null);
  const [detalleId, setDetalleId] = useState<number | null>(null);
  const [moverPedido, setMoverPedido] = useState<{ id: number; mesaId: number | null } | null>(null);
  const [juntarPedido, setJuntarPedido] = useState<{ id: number; mesaId: number | null; mesasYa: number[] } | null>(null);
  const [dividirPedidoId, setDividirPedidoId] = useState<number | null>(null);
  const [transferenciaPedido, setTransferenciaPedido] = useState<PedidoVista | null>(null);
  const [filtro, setFiltro] = useState<'activos' | 'todos'>('activos');
  // Sprint 8 RN-S8-09: separación visual clara entre comer aquí y delivery.
  const [vista, setVista] = useState<'comer_aqui' | 'delivery'>('comer_aqui');
  const negocio = useNegocioStore((s) => s.negocio);
  const sesion = useSessionStore((s) => s.sesion);
  /**
   * Sprint 64 — Antes el botón "Cobrar" aparecía para todos y el mesero descubría que no
   * podía DESPUÉS de teclear el efectivo recibido. Ahora la pantalla pregunta lo mismo
   * que va a preguntar el servidor.
   *
   * Se suscribe a `ajustes` (no a `puede`, que es una función estable y no dispararía el
   * re-render): si el dueño enciende el interruptor, el botón aparece sin volver a entrar.
   * Esto es cortesía de interfaz, no control de acceso — quien autoriza es
   * `safeHandleConPermiso` en el servidor.
   */
  const ajustesPermisos = useAjustesPermisos((s) => s.ajustes);
  const puedeCobrar = puedeUsarCanal(sesion?.rol, 'pedidos:cobrar', ajustesPermisos);
  /** Sprint 89 — Quien puede mandar a cocina es quien puede desatorar un pedido pagado. */
  const puedeEnviarACocina = puedeUsarCanal(sesion?.rol, 'pedidos:enviar-cocina', ajustesPermisos);

  /**
   * Sprint 89 — Sacar del limbo un pedido que ya se pagó y nunca salió a cocina.
   *
   * Es la misma llamada que hace el modal de cobro; lo que cambia es que ahora también se puede
   * disparar **después**, que es justo lo que faltaba cuando esa segunda llamada no llegó a
   * ocurrir.
   */
  const mandarACocina = async (p: PedidoVista) => {
    const r = await window.api.pedidos.enviarACocina(p.id);
    if (!r.ok) {
      await window.alertar({
        titulo: 'No se pudo mandar a cocina',
        mensaje: traducirErrorBackend(r.error, r.codigo),
        variant: 'warn',
      });
      return;
    }
    await cargar();
  };

  const cargar = async () => {
    setCargando(true);
    if (filtro === 'activos') {
      // Sprint 7.2: 'abierto' incluido para mostrar deliveries en pendiente_pago.
      const res = await window.api.pedidos.listarPorEstado([
        'abierto',
        'pendiente_cocina',
        'cocina',
        'listo',
        'entregado',
      ]);
      if (res.ok) setPedidos(res.data);
    } else {
      const res = await window.api.pedidos.listar();
      if (res.ok) setPedidos(res.data);
    }
    setCargando(false);
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtro]);

  // Default tab = la vista con más pedidos en la primera carga.
  const [vistaInicializada, setVistaInicializada] = useState(false);
  useEffect(() => {
    if (vistaInicializada || cargando || pedidos.length === 0) return;
    const ca = pedidos.filter((p) => p.tipo === 'comer_aqui').length;
    const d = pedidos.filter((p) => p.tipo === 'delivery').length;
    setVista(d > ca ? 'delivery' : 'comer_aqui');
    setVistaInicializada(true);
  }, [pedidos, cargando, vistaInicializada]);

  const onCobrado = async (pdfPath?: string) => {
    setPreviewPdfPath(pdfPath ?? null);
    setPreviewId(cobroId);
    setCobroId(null);
    cargar();
  };

  // Sprint 29 — Volver a juntar una cuenta dividida en un solo pedido.
  const reunificar = async (p: PedidoVista) => {
    if (!sesion) return;
    const hermanos = pedidos.filter((x) => x.pedidoPadreId === p.pedidoPadreId).length;
    const ok = await window.confirmar({
      titulo: 'Volver a juntar la cuenta',
      mensaje:
        `Los ${hermanos || ''} sub-pedidos de esta división se reunirán en una sola cuenta` +
        ' (el folio original). Esto no se puede usar si alguno ya fue cobrado.',
      textoConfirmar: 'Sí, juntar',
      textoCancelar: 'Cancelar',
    });
    if (!ok) return;
    const res = await window.api.pedidos.reunificarCuenta({
      pedidoId: p.id,
      usuarioId: sesion.usuarioId,
    });
    if (!res.ok) {
      window.alertar({ titulo: 'No se pudo juntar', mensaje: res.error, variant: 'warn' });
      return;
    }
    cargar();
  };

  const monedaSimbolo = negocio?.monedaSimbolo ?? '$';
  const locale = negocio?.locale ?? 'es-MX';

  // Sprint 8 RN-S8-09: separación visual clara entre comer aquí y delivery.
  const pedidosComerAqui = pedidos.filter((p) => p.tipo === 'comer_aqui');
  const pedidosDelivery = pedidos.filter((p) => p.tipo === 'delivery');
  const pedidosVisibles = vista === 'comer_aqui' ? pedidosComerAqui : pedidosDelivery;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between" data-tour="pedidos-header">
        <div className="flex items-center gap-3">
          <Receipt className="h-7 w-7 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Pedidos</h1>
            <p className="text-sm text-slate-500">
              {pedidosVisibles.length} pedido{pedidosVisibles.length !== 1 ? 's' : ''} en esta vista
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <select
            className="input"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value as 'activos' | 'todos')}
          >
            <option value="activos">Activos</option>
            <option value="todos">Todos</option>
          </select>
          <button onClick={cargar} className="btn-ghost" aria-label="Refrescar">
            <RefreshCw className="h-4 w-4" />
          </button>
          <HelpButton tour={tourPedidosActivos} />
        </div>
      </div>

      {/*
        Sprint 9.2 — Tabs cápsula (Fitts compliance, ≥48px de alto, contraste fuerte
        relleno/vacío entre activo e inactivo). Reemplaza el patrón underline previo
        que la dueña detectó como poco distinguible.
      */}
      <div className="flex flex-wrap gap-2" data-tour="pedidos-tabs-tipo">
        <button
          onClick={() => setVista('comer_aqui')}
          className={`inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold transition shadow-sm ${
            vista === 'comer_aqui'
              ? 'bg-primary text-white shadow-md scale-[1.02]'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          <span className="text-lg leading-none">🍽️</span>
          <span>Comer aquí</span>
          <span
            className={`inline-flex items-center justify-center min-w-[1.75rem] rounded-full px-2 py-0.5 text-xs font-bold ${
              vista === 'comer_aqui'
                ? 'bg-white/25 text-white'
                : 'bg-white text-slate-700'
            }`}
          >
            {pedidosComerAqui.length}
          </span>
        </button>
        <button
          onClick={() => setVista('delivery')}
          className={`inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold transition shadow-sm ${
            vista === 'delivery'
              ? 'bg-primary text-white shadow-md scale-[1.02]'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          <span className="text-lg leading-none">🏍️</span>
          <span>A domicilio</span>
          <span
            className={`inline-flex items-center justify-center min-w-[1.75rem] rounded-full px-2 py-0.5 text-xs font-bold ${
              vista === 'delivery'
                ? 'bg-white/25 text-white'
                : 'bg-white text-slate-700'
            }`}
          >
            {pedidosDelivery.length}
          </span>
        </button>
      </div>

      {/*
        Sprint 99 — Aquí los domicilios se **consultan**; se atienden en el tablero.

        Ésta es la tercera pantalla que mostraba los mismos pedidos a domicilio, y la pregunta del
        dueño del producto fue exacta: *"¿por qué siguen existiendo en vez de controlarlo todo desde
        A domicilio? Una fuente única de verdad"*. La tiene: el tablero, que modela las esperas, los
        minutos y ahora también el remate del pedido.

        La pestaña no se borra porque esta tabla **sí** hace algo que el tablero no: enseña el día
        completo —incluidos los ya entregados y cobrados— y desde ahí se reimprime un ticket. Lo que
        se quita es la duda de cuál manda.

        Los botones de operación se dejan a propósito: quitarlos en el mismo sprint que mueve el
        flujo sería cerrar una salida antes de comprobar que la nueva cubre todos los casos. Van a
        deuda, no a este diff.
      */}
      {vista === 'delivery' && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3">
          <p className="text-sm text-blue-900">
            <span className="font-semibold">Los pedidos a domicilio se atienden en “A domicilio”.</span>{' '}
            Ahí ves qué espera cada uno y desde hace cuánto. Esta lista es para consultar el día.
          </p>
          <button onClick={() => navigate('/domicilios')} className="btn-primary min-h-[44px] text-sm">
            <Bike className="h-4 w-4" />
            Ir al tablero
          </button>
        </div>
      )}

      <div className="card overflow-hidden p-0" data-tour="pedidos-tabla">
        <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase text-slate-500">
            <tr>
              <th className="px-4 py-3">Folio</th>
              <th className="px-4 py-3">Tipo</th>
              <th className="px-4 py-3">Cliente / Mesa</th>
              <th className="px-4 py-3">Hora</th>
              <th className="px-4 py-3">Estado</th>
              <th className="px-4 py-3 text-right">Total</th>
              <th className="px-4 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {cargando ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Cargando...</td></tr>
            ) : pedidosVisibles.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">
                {vista === 'comer_aqui'
                  ? 'No hay pedidos para comer aquí.'
                  : 'No hay pedidos a domicilio.'}
              </td></tr>
            ) : (
              pedidosVisibles.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono font-bold">
                    #{String(p.numeroPedido).padStart(5, '0')}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {/*
                      Sprint 116 — Tres formas de vender, también aquí.

                      Era binario desde antes de que existiera el mostrador, así que la lista
                      llamaba **"🍽️ Mesa"** a una venta para llevar. Tercera puerta del mismo
                      defecto: el Sprint 111 lo cerró en el carrito, el 112 en el cobro y en el
                      banner de cocina. `paraRecoger` manda sobre `tipo`, que para un pedido de
                      mostrador vale `comer_aqui` por herencia del modelo viejo.
                    */}
                    {p.paraRecoger ? '🛍️' : p.tipo === 'comer_aqui' ? '🍽️' : '🏍️'}{' '}
                    {p.paraRecoger ? 'Mostrador' : p.tipo === 'comer_aqui' ? 'Mesa' : 'A domicilio'}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {p.clienteNombre ?? (p.mesaId ? `Mesa #${p.mesaId}` : '—')}
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">
                    {formatFecha(p.fechaCreacion, locale)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      {/*
                        Sprint 89 — La etiqueta ya no sale de un diccionario que sólo mira
                        `estado`. Ese diccionario traducía 'abierto' como "Pago pendiente", y
                        'abierto' **no habla del pago**: un domicilio ya cobrado sigue abierto
                        hasta que alguien lo manda a cocina. La misma fila llegó a decir "Pago
                        pendiente" y "Ya pagado" a la vez.
                      */}
                      <span className={`rounded px-2 py-0.5 text-xs w-fit ${etiquetaEstadoPedido(p).tono}`}>
                        {etiquetaEstadoPedido(p).texto}
                      </span>
                      {p.cuentaSolicitada && p.estado !== 'pagado' && p.estado !== 'cancelado' && (
                        <span className="rounded bg-rose-100 text-rose-700 px-2 py-0.5 text-xs w-fit flex items-center gap-1">
                          <Bell className="h-3 w-3" />
                          Cuenta pedida
                        </span>
                      )}
                      {/* Sprint 29 — marca visible de que esta fila es un sub-pedido de una división. */}
                      {/*
                        Sprint 68 — El chip dice CUÁNTAS faltan, no sólo que está dividida.
                        "Cuenta dividida" no le dice al cajero si ya cobró todo; "faltan 3 de 4"
                        sí. Se calcula de los pedidos que ya están en pantalla —son hermanos con el
                        mismo padre— así que no cuesta una consulta extra.
                      */}
                      {p.pedidoPadreId && p.estado !== 'pagado' && p.estado !== 'cancelado' && (
                        <span className="rounded bg-indigo-100 text-indigo-700 px-2 py-0.5 text-xs w-fit flex items-center gap-1">
                          <Combine className="h-3 w-3" />
                          {(() => {
                            const familia = pedidos.filter(
                              (x) => x.pedidoPadreId === p.pedidoPadreId && x.estado !== 'cancelado',
                            );
                            const faltan = familia.filter((x) => x.estado !== 'pagado').length;
                            return familia.length > 1
                              ? `Dividida · faltan ${faltan} de ${familia.length}`
                              : 'Cuenta dividida';
                          })()}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono font-bold">
                    {formatPrecio(p.total, monedaSimbolo, locale)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => setDetalleId(p.id)}
                        className="btn-ghost p-2"
                        aria-label="Ver items"
                        title="Ver items / cancelar item"
                      >
                        <ListChecks className="h-4 w-4" />
                      </button>
                      {p.tipo === 'comer_aqui' && p.estado !== 'pagado' && p.estado !== 'cancelado' && (
                        <>
                          {/* Sprint 28 — agregar productos a la cuenta de la mesa */}
                          <button
                            onClick={() => navigate(`/pos?agregar=${p.id}`)}
                            className="btn-ghost p-2"
                            aria-label="Agregar productos"
                            title="Agregar más productos a esta cuenta"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => setMoverPedido({ id: p.id, mesaId: p.mesaId ?? null })}
                            className="btn-ghost p-2"
                            aria-label="Mover mesa"
                            title="Mover a otra mesa"
                          >
                            <ArrowRightLeft className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() =>
                              setJuntarPedido({
                                id: p.id,
                                mesaId: p.mesaId ?? null,
                                mesasYa: (p.mesaIds ?? []).filter((id) => id !== p.mesaId),
                              })
                            }
                            className="btn-ghost p-2"
                            aria-label="Juntar mesas"
                            title="Juntar mesas"
                          >
                            <LinkIcon className="h-4 w-4" />
                          </button>
                        </>
                      )}
                      {p.estado !== 'pagado' && p.estado !== 'cancelado' && p.total > 0 && !p.pedidoPadreId && (
                        <button
                          onClick={() => setDividirPedidoId(p.id)}
                          className="btn-ghost p-2"
                          aria-label="Dividir cuenta"
                          title="Dividir cuenta por items"
                        >
                          <Split className="h-4 w-4" />
                        </button>
                      )}
                      {/* Sprint 29 — Volver a juntar: solo en sub-pedidos de una división.
                          Botón con texto (no ícono suelto) para que sea descubrible. */}
                      {p.pedidoPadreId && p.estado !== 'pagado' && p.estado !== 'cancelado' && (
                        <button
                          onClick={() => reunificar(p)}
                          className="btn-primary px-3 py-1 text-xs bg-indigo-600 hover:bg-indigo-700"
                          aria-label="Volver a juntar"
                          title="Volver a juntar esta cuenta dividida en un solo pedido"
                        >
                          <Combine className="h-4 w-4" />
                          Volver a juntar
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setPreviewPdfPath(null);
                          setPreviewId(p.id);
                        }}
                        className="btn-ghost p-2"
                        aria-label="Ver ticket"
                        title="Ver ticket"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      {p.comprobantePath && (
                        <button
                          onClick={async () => {
                            const r = await window.api.pedidos.abrirComprobante(p.id);
                            if (!r.ok) window.alertar({ titulo: 'Error', mensaje: r.error, variant: 'warn' });
                          }}
                          className="btn-ghost p-2"
                          aria-label="Ver comprobante"
                          title="Ver comprobante de pago"
                        >
                          <FileImage className="h-4 w-4 text-emerald-600" />
                        </button>
                      )}
                      {p.tipo === 'delivery' && p.estado === 'abierto' && p.estadoPago === 'pendiente_pago' && (
                        <button
                          onClick={() => setTransferenciaPedido(p)}
                          className="btn-primary px-3 py-1 text-xs bg-amber-600 hover:bg-amber-700"
                          aria-label="Adjuntar comprobante"
                          title="Subir comprobante de transferencia"
                        >
                          <Upload className="h-4 w-4" />
                          Comprobante
                        </button>
                      )}
                      {/*
                        Sprint 64 — Sólo a quien de verdad puede cerrar la cuenta.
                        Sprint 88 — Y sólo si de verdad falta cobrar.

                        Reporte del negocio: un domicilio pagado por transferencia y ya en cocina
                        seguía mostrando "Cobrar". El servidor rechaza el segundo cobro desde el
                        Sprint 69, pero el botón invitaba a **pedirle el dinero otra vez al
                        cliente**, y eso pasa antes de que el sistema alcance a decir que no.
                      */}
                      {puedeCobrar && seLePuedeCobrar(p) && (
                        <button
                          onClick={() => setCobroId(p.id)}
                          className="btn-primary px-3 py-1 text-xs"
                          aria-label="Cobrar"
                        >
                          <DollarSign className="h-4 w-4" />
                          Cobrar
                        </button>
                      )}
                      {/*
                        Sprint 89 — La salida del limbo.

                        Un pedido pagado que se quedó 'abierto' —porque el envío a cocina falló o
                        alguien cerró la ventana a media operación— no tenía ninguna puerta: sin
                        "Cobrar" (ya tiene pago), sin "Comprobante" (ya está pagado) y sin nada que
                        lo mandara a hacer. Dinero cobrado y comida que nunca se prepara.
                      */}
                      {puedeEnviarACocina && etiquetaEstadoPedido(p).accion === 'enviar_cocina' && (
                        <button
                          onClick={() => mandarACocina(p)}
                          className="btn-primary px-3 py-1 text-xs bg-orange-600 hover:bg-orange-700"
                          aria-label="Mandar a cocina"
                          title="Ya está pagado: mándalo a preparar"
                        >
                          <ChefHat className="h-4 w-4" />
                          Mandar a cocina
                        </button>
                      )}
                      {/*
                        El "ya pagado" sólo cuando la columna de estado no lo dice ya: dos textos
                        para el mismo hecho es exactamente lo que produjo la contradicción.
                      */}
                      {/*
                        Sprint 141 — "Ya pagado" dejó de ser siempre cierto.

                        Desde que a un pedido cobrado se le pueden agregar productos, la misma fila
                        podía decir "$340" y "✓ Ya pagado" con $145 sin cobrar. Es la misma
                        contradicción que este archivo documenta arriba —"Pago pendiente" junto a
                        "Ya pagado"—, sólo que al revés y con el dinero del lado del negocio.
                      */}
                      {puedeCobrar &&
                        motivoSinCobro(p) === 'ya_pagado' &&
                        etiquetaEstadoPedido(p).accion === null &&
                        ((p.faltaCobrar ?? 0) > 0 ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800">
                            Falta cobrar {formatPrecio(p.faltaCobrar ?? 0, monedaSimbolo, locale)}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700">
                            <Check className="h-3.5 w-3.5" />
                            Ya pagado
                          </span>
                        ))}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      <CobroModal
        abierto={cobroId !== null}
        pedidoId={cobroId}
        onClose={() => setCobroId(null)}
        onCobrado={onCobrado}
      />

      <TicketPreviewModal
        abierto={previewId !== null}
        pedidoId={previewId}
        pdfPath={previewPdfPath}
        onClose={() => {
          setPreviewId(null);
          setPreviewPdfPath(null);
        }}
      />

      <DetallePedidoModal
        abierto={detalleId !== null}
        pedidoId={detalleId}
        onClose={() => setDetalleId(null)}
        onItemCancelado={cargar}
      />

      <MoverMesaModal
        abierto={moverPedido !== null}
        pedidoId={moverPedido?.id ?? null}
        mesaActualId={moverPedido?.mesaId ?? null}
        onClose={() => setMoverPedido(null)}
        onMovido={cargar}
      />

      <JuntarMesasModal
        abierto={juntarPedido !== null}
        pedidoId={juntarPedido?.id ?? null}
        mesaPrincipalId={juntarPedido?.mesaId ?? null}
        mesasYaUnidas={juntarPedido?.mesasYa ?? []}
        onClose={() => setJuntarPedido(null)}
        onUnido={cargar}
      />

      <DividirCuentaModal
        abierto={dividirPedidoId !== null}
        pedidoId={dividirPedidoId}
        onClose={() => setDividirPedidoId(null)}
        onDividido={cargar}
      />

      <CobroTransferenciaModal
        abierto={transferenciaPedido !== null}
        pedido={transferenciaPedido}
        onClose={() => setTransferenciaPedido(null)}
        onConfirmado={() => {
          setTransferenciaPedido(null);
          cargar();
        }}
      />
    </div>
  );
}
